import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminProductAPI } from '../services/api';
import '../styles/ProductView.css';

// Helper function to convert Google Drive sharing links to direct image URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return '';

  // Trim the URL to remove any whitespace
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return '';

  // Check if it's a Google Drive link
  const driveMatch = trimmedUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch && driveMatch[1]) {
    // Convert to direct download format
    return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  }

  // Return original URL if it's not a Google Drive link
  return url;
};

const ProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProduct = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminProductAPI.getById(id);
      setProduct(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.response?.data?.message || 'Failed to fetch product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleBack = () => {
    navigate('/products');
  };

  const handleEdit = () => {
    navigate(`/products/${id}/edit`);
  };

  if (loading) {
    return (
      <div className="product-view-container">
        <div className="loading">Loading product details...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-view-container">
        <div className="error-message">{error || 'Product not found'}</div>
        <button className="btn btn-primary" onClick={handleBack}>
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div className="product-view-container">
      {/* Header with images */}
      <div className="product-view-header">
        <div className="header-images">
          <div className="header-image">
            {product.productImage ? (
              <img
                src={convertGoogleDriveUrl(product.productImage)}
                alt="Product"
                onError={(e) => {
                  console.error('Image 1 failed to load:', product.productImage);
                  console.error('Converted URL:', convertGoogleDriveUrl(product.productImage));
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="no-image">Image failed to load</div>';
                }}
              />
            ) : (
              <div className="no-image">No Image</div>
            )}
          </div>
          <div className="header-image">
            {product.productImage2 ? (
              <img
                src={convertGoogleDriveUrl(product.productImage2)}
                alt="Product"
                onError={(e) => {
                  console.error('Image 2 failed to load:', product.productImage2);
                  console.error('Converted URL:', convertGoogleDriveUrl(product.productImage2));
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="no-image">Image failed to load</div>';
                }}
              />
            ) : (
              <div className="no-image">No Image</div>
            )}
          </div>
        </div>
      </div>

      {/* Product Title and Actions */}
      <div className="product-title-section">
        <div className="title-left">
          <h1>{product.productName}</h1>
          <div className="product-meta">
            <span className="meta-label">Catalogue Number:</span>
            <span className="meta-value catalog-number">{product.catalogNumber}</span>
            <span className="meta-label">Category:</span>
            {product.productCategory && (
              <span className="category-link">{product.productCategory}</span>
            )}
          </div>
        </div>
        <div className="title-actions">
          <button className="btn btn-secondary" onClick={handleBack}>
            Back to List
          </button>
          <button className="btn btn-primary" onClick={handleEdit}>
            Edit Product
          </button>
        </div>
      </div>

      {/* Description */}
      {product.description && (
        <div className="product-description">
          <p>{product.description}</p>
        </div>
      )}

      {/* Product Details Grid */}
      <div className="product-details-grid">
        {/* Left Column */}
        <div className="details-column">
          {product.packSize && (
            <div className="detail-item">
              <span className="detail-label">Size</span>
              <span className="detail-value">{product.packSize}</span>
            </div>
          )}

          {product.reactivity && (
            <div className="detail-item">
              <span className="detail-label">Reactivity</span>
              <span className="detail-value">{product.reactivity}</span>
            </div>
          )}

          {product.standard && (
            <div className="detail-item">
              <span className="detail-label">Standard</span>
              <span className="detail-value">{product.standard}</span>
            </div>
          )}

          {product.sampleType && (
            <div className="detail-item">
              <span className="detail-label">Sample Type</span>
              <span className="detail-value">{product.sampleType}</span>
            </div>
          )}

          {product.storage && (
            <div className="detail-item">
              <span className="detail-label">Storage</span>
              <span className="detail-value">{product.storage}</span>
            </div>
          )}

          {product.dilutions && (
            <div className="detail-item">
              <span className="detail-label">Dilutions</span>
              <span className="detail-value">{product.dilutions}</span>
            </div>
          )}

          {product.isotype && (
            <div className="detail-item">
              <span className="detail-label">Isotype</span>
              <span className="detail-value">{product.isotype}</span>
            </div>
          )}

          {product.immunogen && (
            <div className="detail-item">
              <span className="detail-label">Immunogen</span>
              <span className="detail-value">{product.immunogen}</span>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="details-column">
          {product.brandName && (
            <div className="detail-item">
              <span className="detail-label">Brand Name</span>
              <span className="detail-value">{product.brandName}</span>
            </div>
          )}

          {product.sensitivity && (
            <div className="detail-item">
              <span className="detail-label">Sensitivity</span>
              <span className="detail-value">{product.sensitivity}</span>
            </div>
          )}

          {product.detectionRange && (
            <div className="detail-item">
              <span className="detail-label">Detection Range</span>
              <span className="detail-value">{product.detectionRange}</span>
            </div>
          )}

          {product.assayLength && (
            <div className="detail-item">
              <span className="detail-label">Assay Length</span>
              <span className="detail-value">{product.assayLength}</span>
            </div>
          )}

          {product.source && (
            <div className="detail-item">
              <span className="detail-label">Source</span>
              <span className="detail-value">{product.source}</span>
            </div>
          )}

          {product.clonality && (
            <div className="detail-item">
              <span className="detail-label">Clonality</span>
              <span className="detail-value">{product.clonality}</span>
            </div>
          )}

          {product.concentration && (
            <div className="detail-item">
              <span className="detail-label">Concentration</span>
              <span className="detail-value">{product.concentration}</span>
            </div>
          )}
        </div>
      </div>

      {/* Application Section */}
      {product.applications && (
        <div className="product-section">
          <h2>Application</h2>
          <p>{product.applications}</p>
        </div>
      )}

      {/* Alternative Names */}
      {product.alternativeNames && (
        <div className="product-section">
          <h2>Alternative Names</h2>
          <p>{product.alternativeNames}</p>
        </div>
      )}

      {/* Research Area */}
      {product.researchArea && (
        <div className="product-section">
          <h2>Research Area</h2>
          <p>{product.researchArea}</p>
        </div>
      )}

      {/* Test Principle */}
      {product.testPrinciple && (
        <div className="product-section">
          <h2>Test Principle</h2>
          <p>{product.testPrinciple}</p>
        </div>
      )}

      {/* Product Documentation */}
      {product.productInsert && (
        <div className="product-section">
          <h2>Product Documentation</h2>
          <a
            href={product.productInsert}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-download"
          >
            Download Product Manual
          </a>
        </div>
      )}

      {/* SEO Settings Panel */}
      {(product.seoTitle || product.seoDescription || product.seoImage) && (
        <div className="product-section pv-seo-section">
          <h2>SEO Settings</h2>
          <p className="pv-seo-note">
            These values override the auto-generated SEO for this product page.
          </p>

          {/* Google Snippet Preview */}
          <div className="pv-seo-snippet-wrap">
            <p className="pv-seo-snippet-label">Google search preview</p>
            <div className="pv-seo-snippet">
              <p className="pv-snippet-title">
                {product.seoTitle || `${product.productName} | EC Biolabs`}
              </p>
              <p className="pv-snippet-url">
                ecbiolabs.com/products/{product.catalogNumber}
              </p>
              <p className="pv-snippet-desc">
                {product.seoDescription || product.description || 'No meta description set.'}
              </p>
            </div>
          </div>

          <div className="pv-seo-fields">
            <div className="pv-seo-field">
              <span className="pv-seo-field-label">SEO Title</span>
              <span className="pv-seo-field-value">
                {product.seoTitle ? (
                  <>
                    {product.seoTitle}
                    <span className="pv-seo-char-count">{product.seoTitle.length}/70 chars</span>
                  </>
                ) : (
                  <span className="pv-seo-auto">Auto-generated from product name</span>
                )}
              </span>
            </div>

            <div className="pv-seo-field">
              <span className="pv-seo-field-label">SEO Description</span>
              <span className="pv-seo-field-value">
                {product.seoDescription ? (
                  <>
                    {product.seoDescription}
                    <span className="pv-seo-char-count">{product.seoDescription.length}/200 chars</span>
                  </>
                ) : (
                  <span className="pv-seo-auto">Auto-generated from product description</span>
                )}
              </span>
            </div>

            {product.seoImage && (
              <div className="pv-seo-field pv-seo-field--image">
                <span className="pv-seo-field-label">OG Image</span>
                <div className="pv-seo-og-wrap">
                  <img
                    src={product.seoImage}
                    alt="OG preview"
                    className="pv-seo-og-img"
                    onError={e => e.target.style.display = 'none'}
                  />
                  <span className="pv-seo-og-url">{product.seoImage}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductView;
